import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

// Two-step, PIN-gated removal of exact-duplicate templates (same artwork).
// Mirrors usePurgeUnminted: step 1 loads a visual review of the duplicate
// groups, step 2 verifies the admin passcode, step 3 confirms the removal.
export const useDedupeTemplates = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [review, setReview] = useState(null);   // { group_count, removable_total, groups[] }
  const [preview, setPreview] = useState(null);  // { group_count, removable_count, confirmation_token }
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const loadReview = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get("/admin/templates/dedupe/groups");
      setReview(data);
    } catch (e) {
      setError(apiError(e, "Could not scan for duplicates."));
    } finally {
      setBusy(false);
    }
  };

  const changeOpen = (next) => {
    if (inFlight.current) return;
    if (!next && preview?.confirmation_token) {
      api.delete("/admin/templates/dedupe/confirmation", {
        data: { confirmation_token: preview.confirmation_token },
      }).catch(() => { /* The unused approval also expires server-side. */ });
    }
    setPin("");
    setPreview(null);
    setError("");
    setReview(null);
    setOpen(next);
    if (next) loadReview();
  };

  const verify = async (event) => {
    event.preventDefault();
    if (inFlight.current || !/^[0-9]{4,10}$/.test(pin)) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/templates/dedupe/prepare", { pin });
      setPreview(data);
    } catch (e) {
      setError(apiError(e, "Passcode verification failed. Nothing was removed."));
    } finally {
      setPin("");
      inFlight.current = false;
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (inFlight.current || !preview?.confirmation_token || !preview.removable_count) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/templates/dedupe", {
        confirmation_token: preview.confirmation_token, confirmed: true,
      });
      ["admin-templates", "admin-listings", "listings", "home-featured"].forEach((key) => {
        qc.invalidateQueries({ queryKey: [key] });
      });
      toast.success(data.deleted ? `Removed ${data.deleted} duplicate template${data.deleted === 1 ? "" : "s"}` : "No duplicates removed", {
        id: "dedupe-result",
        description: data.skipped ? `${data.skipped} reviewed items were no longer eligible.` : undefined,
      });
      setOpen(false);
      setPreview(null);
      setReview(null);
    } catch (e) {
      setPreview(null);
      setError(`${apiError(e, "Removal could not be confirmed.")} Review the duplicates before trying again.`);
      qc.invalidateQueries({ queryKey: ["admin-templates"] });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return { open, changeOpen, pin, setPin, review, preview, error, busy, verify, confirm };
};
